import type {
  DashboardExtra,
  HuongTangTruong,
  MonetizationStatus,
  TheoTangTruong,
} from '@qlk/shared';
import {
  MONETIZATION_STATUSES,
  NGAY_NHAC_CHOT,
  PRODUCTION_STAGE_LABEL,
  SCHEDULED_POST_STATUSES,
  SCHEDULED_POST_STATUS_SHORT,
  nhanKyDoiSoat,
  type DashboardSummary,
} from '@qlk/shared';
import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import { thangHienTai, useKyChung } from '../../../layout/kyChung.js';
import { dungMenu } from '../../../layout/menu.js';
import { coQuyen, useToi, useXemTien } from '../../../lib/auth.js';
import { nhanSoKyTruoc, type KyTrang } from '../../../lib/kyTrang.js';
import { NHOM_MENU } from '../../../lib/modules.js';
import {
  CHUA_DU_LUOT_XEM,
  EmptyState,
  gioPhut,
  KhoiChuaCoKenh,
  Nut,
  PageHeader,
  StatStrip,
  type OChiSo,
} from '../../../ui/index.js';
import {
  duongDanXuatBaoCao,
  useCanhBao,
  useSoNhapDoiSoat,
  useTangTruong7Ngay,
  useThem,
  useTongQuan,
} from '../api.js';
import { BangTopThucNhan } from '../components/BangTopThucNhan.js';
import { DongTomTat } from '../components/DongTomTat.js';
import { KhoiDongTien } from '../components/KhoiDongTien.js';
import { KhoiNguonTien } from '../components/KhoiNguonTien.js';
import { KhungCanXuLy } from '../components/KhungCanXuLy.js';
import { KhungTangTruong } from '../components/KhungTangTruong.js';
import { gonSo, huongDelta, phanTram } from '../components/dinhDang.js';
import { chiSoLuotXem } from '../components/luotXem.js';

/**
 * Trang chủ `/`.
 *
 * Bố cục (29/10/2026 — xem `docs/ui.md` mục 18):
 *
 * 1. thanh **4 ô vận hành** (lượt xem, subscribers, kênh kiếm tiền, đối soát);
 * 2. khối **Dòng tiền**: con số cuối chuỗi + thanh "Doanh thu gộp đi về đâu";
 * 3. "Tăng trưởng lượt xem · 7 ngày" (3/5) cạnh "Cần xử lý" (2/5);
 * 4. bảng kênh (3/5) cạnh "Kênh theo nguồn tiền" (2/5);
 * 5. hai dòng tóm tắt Sản xuất / Kho nhạc, chỉ khi có số.
 *
 * Hai thay đổi so với bản 26/10/2026:
 *
 * - **Thanh 4 ô lên đầu.** Nó là bốn con số người ta mở trang để nhìn trước;
 *   khối Dòng tiền là một chuỗi phép trừ, đọc nó cần dừng lại lâu hơn.
 * - **Khối "Lượt xem toàn hệ thống" bỏ hẳn.** Nó vẽ một đường cho cả hệ thống,
 *   nói "tuần này nhiều hơn 2,1%" mà không nói kênh nào, nên không ai làm được
 *   gì sau khi đọc. Chỗ đó nay là bảng xếp hạng 5 kênh đổi nhiều nhất; con số
 *   tổng vẫn ở ô đầu của thanh 4 ô, cùng hàm dựng (`components/luotXem.ts`).
 *
 * Bản trước nữa xếp **bảy** ô ngang hàng, trong đó bốn ô là bốn chặng của cùng
 * một phép trừ (Công ty thực nhận → Phần chia Network → Chia nhân sự → Công ty
 * giữ lại). Bốn ô đó nay là **một** khối có thanh tỷ trọng; thanh ô còn lại chỉ
 * nói về vận hành.
 *
 * Mỗi khối có skeleton và trạng thái lỗi riêng: một module hỏng thì phần còn
 * lại của trang vẫn dùng được (hợp đồng docs/modules/dashboard.md).
 */

/** Nhãn ngắn của tình trạng kiếm tiền, dùng trong dòng phụ của dải chỉ số. */
const NHAN_NGAN_KIEM_TIEN: Record<MonetizationStatus, string> = {
  not_eligible: 'chưa đủ ĐK',
  eligible: 'đủ ĐK, chưa nộp',
  under_review: 'chờ xét duyệt',
  monetized: 'đang kiếm tiền',
  rejected: 'bị từ chối',
  suspended: 'bị tắt kiếm tiền',
};

/**
 * Dòng phụ dưới tiêu đề: "12 kênh · 9 Network + AdSense · đồng bộ lần cuối
 * 09:25". Phần AdSense chỉ hiện khi thật sự có đối tác AdSense trực tiếp
 * (`partners` nhiều hơn số hợp đồng Network).
 */
function moTaTrang(d: DashboardSummary | undefined, lastSyncAt: string | null | undefined): string {
  const phan: string[] = [];

  if (d?.channels) phan.push(`${String(d.channels.total)} kênh`);

  if (d?.networks) {
    const soNetwork = d.networks.activeContracts;
    const coAdsense = d.networks.partners > soNetwork;
    phan.push(`${String(soNetwork)} Network${coAdsense ? ' + AdSense' : ''}`);
  }

  if (lastSyncAt !== null && lastSyncAt !== undefined) {
    phan.push(`đồng bộ lần cuối ${gioPhut(lastSyncAt)}`);
  }

  return phan.join(' · ');
}

/**
 * Phần `delta` của một ô chỉ số. Thiếu dữ liệu kỳ trước thì không hiện gì.
 *
 * Nhãn đi qua `nhanSoKyTruoc`: kỳ chưa trọn ghi rõ khoảng đã so, vì kỳ trước
 * của nó là **cùng số ngày** của tháng trước chứ không phải cả tháng.
 */
function soVoiKyTruoc(
  phanTramDelta: number | null | undefined,
  ky: KyTrang | null | undefined,
): Pick<OChiSo, 'delta'> {
  if (phanTramDelta === null || phanTramDelta === undefined) return {};
  return {
    delta: {
      huong: huongDelta(phanTramDelta),
      text: `${phanTram(phanTramDelta)} ${nhanSoKyTruoc(ky)}`,
    },
  };
}

/**
 * Dòng phụ của ô "Kênh kiếm tiền":
 * `6 có doanh thu kỳ này · 1 bị từ chối · 1 chưa đủ ĐK`.
 *
 * Con số đầu là **luật khác** với tử số của ô (xem {@link TrangTongQuan}), nên
 * nó phải tự nói ra mình đếm gì; các con số sau là tình trạng YPP nhập tay,
 * cùng rổ với tử số nên cộng lại đúng bằng tổng số kênh đang hoạt động.
 */
function moTaKiemTien(
  them: DashboardExtra | undefined,
  coDoanhThu: number | undefined,
): string | undefined {
  const phan: string[] = [];

  if (coDoanhThu !== undefined) phan.push(`${String(coDoanhThu)} có doanh thu kỳ này`);

  if (them) {
    phan.push(
      ...MONETIZATION_STATUSES.filter(
        (t) => t !== 'monetized' && (them.theoKiemTien[t] ?? 0) > 0,
      ).map((t) => `${String(them.theoKiemTien[t])} ${NHAN_NGAN_KIEM_TIEN[t]}`),
    );
  }

  return phan.length > 0 ? phan.join(' · ') : undefined;
}

/** "2 đã đăng · 1 đã lên lịch · 1 chờ kiểm duyệt" — theo `SCHEDULED_POST_STATUSES`. */
function moTaTheoTrangThai(sx: NonNullable<DashboardSummary['production']>): string {
  const phan = SCHEDULED_POST_STATUSES.filter((t) => (sx.thisWeekByStatus[t] ?? 0) > 0).map(
    (t) => `${String(sx.thisWeekByStatus[t])} ${SCHEDULED_POST_STATUS_SHORT[t]}`,
  );

  return phan.length > 0 ? phan.join(' · ') : 'Chưa có bài nào trên lịch tuần này';
}

/** Mục menu đầu tiên người dùng xem được, dùng khi họ không có quyền vào Tổng quan. */
function mucDauTienXemDuoc(me: Parameters<typeof coQuyen>[0]): string | null {
  const menu = dungMenu();
  for (const nhom of NHOM_MENU) {
    for (const m of menu[nhom]) {
      if (m.to === '/' || m.chuaCai) continue;
      if (coQuyen(me, m.permission, 'view')) return m.to;
    }
  }
  return null;
}

export function TrangTongQuan() {
  const toi = useToi();
  /*
   * Kỳ lấy từ **ô chọn chung** ở thanh trên (`?ky=`), không còn state riêng:
   * hai ô cùng hỏi một câu mà trả lời khác nhau là lỗi người dùng gặp đầu tiên.
   * Trang khai kiểu `ngay` trong `layout/kyChung.ts`.
   */
  const ky = useKyChung();
  /* Kỳ ngoài mốc dữ liệu: dải chỉ số hiện `—` thay vì số 0 của API. */
  const kyRong = ky.kyRong;
  /* Kiểu `moc` luôn có `ky.ky`; `giaTri` là lối thoát cho lần render đầu. */
  const kyTrang = ky.ky;
  const range = kyTrang?.ma ?? ky.giaTri;
  const nhanKy = kyTrang?.nhan ?? '';
  /** Dòng phụ của trang: `28 ngày qua · 23/09 – 20/10/2026`. */
  const nhanKyKhoang =
    kyTrang && kyTrang.nhanKhoang !== '' ? `${kyTrang.nhan} · ${kyTrang.nhanKhoang}` : nhanKy;

  const duocXem = coQuyen(toi.data?.permissions, 'dashboard', 'view');
  const tomTat = useTongQuan(range);
  const them = useThem(range);
  const canhBao = useCanhBao();

  /*
   * Hai nút chuyển của khung tăng trưởng. Giữ ở state của trang, **không** ở
   * URL: chúng không phải thứ ai cũng muốn chia sẻ qua link, và `?ky=` trên URL
   * này đã có một nghĩa khác (kỳ của cả trang) — thêm hai tham số nữa vào đó là
   * mời người ta dán một link nói ngược lại chính nó.
   */
  const [huongTang, datHuongTang] = useState<HuongTangTruong>('tang');
  const [theoTang, datTheoTang] = useState<TheoTangTruong>('pct');
  const tangTruong = useTangTruong7Ngay(huongTang, theoTang);

  /*
   * Ô "Đối soát" nói về bảng lương của cả công ty nên chỉ `team:admin` thấy —
   * cùng cổng với nhắc "còn N bản nháp" trong khung Cần xử lý. Kỳ của ô là
   * **tháng hiện tại**, không phải kỳ của trang: đối soát chốt theo tháng.
   */
  const quanTriNhanSu = coQuyen(toi.data?.permissions, 'team', 'admin');
  const thangChot = thangHienTai();
  const soNhap = useSoNhapDoiSoat(thangChot, quanTriNhanSu);

  // Cộng tác viên không có `dashboard:view`: đưa thẳng sang trang họ dùng được
  // thay vì chặn bằng màn "Không đủ quyền" ngay sau khi đăng nhập.
  if (toi.data && !duocXem) {
    const dich = mucDauTienXemDuoc(toi.data.permissions);
    if (dich) return <Navigate to={dich} replace />;
    return (
      <EmptyState
        title="Tài khoản chưa được cấp quyền xem màn hình nào"
        description="Liên hệ quản trị viên để được phân quyền."
      />
    );
  }

  const d = tomTat.data;
  const dangTai = tomTat.isPending;
  const loiTomTat = tomTat.error?.message ?? null;
  const loiThem = them.error?.message ?? null;

  const soKenh = d?.channels;
  const khanGia = d?.audience;

  /*
   * "Toàn thời gian" **không có kỳ trước** để so: trước ngày đầu tiên có số
   * liệu thì không có gì cả. Ẩn hẳn phần so sánh thay vì in một mũi tên tính
   * trên một khoảng rỗng.
   */
  const coKyTruoc = kyTrang?.coKyTruoc ?? true;

  /*
   * Phạm vi dữ liệu của người đang xem, **không** suy từ `xemDayDu`:
   * `assignedChannelIds === null` nghĩa là không bị giới hạn kênh. Hai câu hỏi
   * khác nhau — "thấy tiền của ai" (máy chủ trả `xemDayDu`) và "số này của
   * những kênh nào" — nên nhãn ô phải đọc cờ của đúng câu hỏi của nó.
   */
  const toanCongTy = toi.data?.assignedChannelIds === null;
  /*
   * Ra soat bao mat 31/10/2026, muc **S5**: thieu `revenue:view` thi may chu bo
   * han `revenue`, `chuoiTien`, `topThucNhan`, `theoNguonTien` va `chiaNhanSu`
   * khoi dap an. Trang phai bo luon ba khoi doc chung — khong de lai khung rong
   * va khong hien `—`: mot khoi ten "Dong tien" voi mot dai `—` van noi ra o
   * day co so tien.
   */
  const { coXemTien } = useXemTien();
  const chia = coXemTien ? (them.data?.chiaNhanSu ?? null) : null;
  const xemDayDu = chia?.xemDayDu === true;

  /*
   * Tỷ lệ **bình quân** của người đang xem trên lợi nhuận kênh của cả phạm vi.
   *
   * Không phải một tỷ lệ trong hợp đồng nào: người hưởng 7% ở kênh này và 5% ở
   * kênh kia thì con số này nằm giữa. Nó chỉ trả lời "phần của tôi chiếm bao
   * nhiêu lợi nhuận" — đúng câu mà dòng phụ hỏi. Lợi nhuận ≤ 0 (kênh lỗ mà vẫn
   * chia theo thực nhận) thì không có tỷ lệ nào đọc được, trả `null`.
   */
  const pctCuaToi =
    chia !== null && chia.loiNhuan > 0
      ? Math.round((chia.phanCuaToi / chia.loiNhuan) * 1000) / 10
      : null;

  const sanXuat = d?.production;
  const nhac = d?.music;
  /* Toàn số 0 thì **không hiện dòng nào** — một dòng số 0 chỉ dạy người ta bỏ qua. */
  const coNhac = nhac && nhac.ownTracks + nhac.partnerTracks + nhac.expiringSoon > 0;
  const coSanXuat = sanXuat && sanXuat.thisWeek > 0;

  /* ---------------------------- Ô vận hành ---------------------------- */

  /*
   * Lượt xem: **một** nguồn cho ô chỉ số và cho tiêu đề biểu đồ ngay dưới nó.
   * Hai chỗ in cùng một chỉ số của cùng một kỳ thì phải in cùng một chuỗi —
   * xem `components/luotXem.ts` để biết bản cũ lệch những gì.
   */
  const deltaLuotXem = coKyTruoc ? (khanGia?.viewsDeltaPercent ?? null) : null;
  const luotXem = chiSoLuotXem(khanGia?.views, deltaLuotXem);

  const o: OChiSo[] = [
    {
      /*
       * `views === null` = chưa ngày nào trong kỳ có số liệu lượt xem.
       * `gonSo(null)` ra `—`; dòng phụ nói rõ kỳ chỉ phủ được bao nhiêu ngày để
       * không ai mang con số này đi so với YouTube Studio rồi tưởng app sai.
       */
      nhan: `${toanCongTy ? 'Lượt xem' : 'Lượt xem kênh của bạn'} · ${nhanKy}`,
      giaTri: dangTai ? '…' : luotXem.so,
      ...(luotXem.delta === null
        ? {}
        : {
            delta: {
              huong: luotXem.delta.huong,
              text: `${luotXem.delta.pct} ${nhanSoKyTruoc(kyTrang)}`,
            },
          }),
      ...(khanGia && khanGia.views === null
        ? { giaiThich: CHUA_DU_LUOT_XEM }
        : khanGia && khanGia.soNgayCoViews < khanGia.soNgayTrongKy
          ? {
              phu: `chỉ tính ${String(khanGia.soNgayCoViews)}/${String(khanGia.soNgayTrongKy)} ngày có số liệu`,
            }
          : {}),
    },
    {
      /* Subscribers là số tại thời điểm này; `delta` là mức tăng trong kỳ. */
      nhan: `${toanCongTy ? 'Subscribers' : 'Subscribers kênh của bạn'} · hiện tại`,
      giaTri: dangTai ? '…' : gonSo(khanGia?.subscribers),
      /*
       * `ky: null` **có ý**: `subscribersDeltaPercent` là mức tăng **trong kỳ**
       * tính trên số đầu kỳ (subscribers là số tích luỹ — xem
       * `docs/modules/dashboard.md`), không phải phép so với kỳ trước. Ghi ra
       * khoảng `01/09 – 26/09` ở đây là nói sai về con số đang hiện.
       */
      ...soVoiKyTruoc(coKyTruoc ? khanGia?.subscribersDeltaPercent : null, null),
    },
  ];

  if (toanCongTy) {
    o.push({
      /*
       * Tử số = số kênh có **tình trạng YPP** `monetized` (nhập tay), không
       * phải số kênh có doanh thu trong kỳ. Hai luật đó cho hai con số khác
       * nhau và bản trước trộn chúng vào một ô: tử số theo doanh thu, dòng phụ
       * theo YPP — cộng lại không ra tổng, không ai hiểu nổi ô đó nói gì.
       * Nay cả tử số và dòng phụ (trừ số đầu, có ghi rõ) cùng một rổ.
       */
      nhan: 'Kênh kiếm tiền · hiện tại',
      giaiThich:
        'Tử số đếm kênh có tình trạng YPP "Đang kiếm tiền" (quản lý nhập tay từ Studio). Số "có doanh thu kỳ này" ở dòng dưới đếm theo luật khác: đã bật kiếm tiền và có doanh thu gộp lớn hơn 0 trong kỳ — giống trang Doanh thu. Một kênh mới bật kiếm tiền mà kỳ này chưa có tiền sẽ vào tử số nhưng không vào số đó.',
      giaTri: dangTai
        ? '…'
        : them.data && soKenh
          ? `${String(them.data.theoKiemTien.monetized ?? 0)}/${String(soKenh.total)}`
          : '—',
      ...(moTaKiemTien(them.data, soKenh?.monetized) === undefined
        ? {}
        : { phu: moTaKiemTien(them.data, soKenh?.monetized) }),
    });
  }

  if (quanTriNhanSu) {
    const dem = soNhap.data?.demNhap;
    o.push({
      nhan: `Đối soát · ${nhanKyDoiSoat(thangChot)}`,
      /*
       * Giá trị là **một link cao 44px**: ô này là việc phải làm, không phải số
       * để ngắm. `StatStrip` không có khái niệm "ô bấm được" nên chỗ bấm nằm ở
       * chính con số — và nó phải đủ cao theo luật vùng bấm (docs/ui.md mục 1).
       */
      giaTri: (
        <Link
          to={`/doi-soat-nhan-su?ky=${thangChot}`}
          className="inline-flex min-h-11 items-center hover:text-nhan"
        >
          {soNhap.isPending
            ? '…'
            : dem === undefined
              ? '—'
              : dem === 0
                ? 'Đã chốt'
                : `${String(dem)} bản nháp`}
        </Link>
      ),
      /*
       * **Không** hứa "tự chốt ngày …": app không có việc tự chốt nào, chốt là
       * một lượt bấm của quản trị nhân sự. Câu đúng là mốc nhắc
       * (`NGAY_NHAC_CHOT`), cùng con số mà khung Cần xử lý đang dùng.
       */
      phu:
        dem === 0
          ? 'không còn ai chờ chốt'
          : `nhắc chốt từ ngày ${String(NGAY_NHAC_CHOT)} của tháng sau`,
    });
  } else if (coSanXuat) {
    /* Chỉ số vận hành của người xem hẹp — lịch đăng tuần này, số đã có ở
       `/summary`. Không có bài nào thì bỏ ô, không bày một số 0. */
    o.push({
      nhan: 'Trên lịch tuần này',
      giaTri: dangTai ? '…' : String(sanXuat.thisWeek),
      phu: moTaTheoTrangThai(sanXuat),
    });
  }

  /*
   * Bảng kênh: người xem đủ sắp theo **công ty giữ lại** (cột cuối chuỗi), còn
   * năm dòng vẫn do máy chủ chọn theo thực nhận — caption nói rõ điều đó, vì
   * một tiêu đề hứa thứ tự mà không hứa cách chọn thì dễ bị đọc thành "năm kênh
   * giữ lại nhiều nhất".
   */
  const dongBang = them.data?.topThucNhan;
  const dongBangSap =
    xemDayDu && dongBang
      ? [...dongBang].sort((a, b) => (b.congTyGiuLai ?? 0) - (a.congTyGiuLai ?? 0))
      : dongBang;

  return (
    <>
      <PageHeader
        title="Tổng quan"
        phu={`${nhanKyKhoang} · ${moTaTrang(d, them.data?.lastSyncAt)}`}
        actions={
          <a href={duongDanXuatBaoCao(range)} download>
            <Nut kieu="chinh">Xuất báo cáo</Nut>
          </a>
        }
      />

      <KhoiChuaCoKenh tenTrang="Tổng quan" />

      {loiTomTat && (
        <div className="mb-4 rounded-the border border-vien bg-be-mat">
          <EmptyState
            loi
            title="Không tải được số liệu tổng quan"
            description={loiTomTat}
            action={
              <Nut
                kieu="phu"
                onClick={() => {
                  void tomTat.refetch();
                }}
              >
                Thử lại
              </Nut>
            }
          />
        </div>
      )}

      {/*
        Thanh 4 ô vận hành đứng **trên** khối Dòng tiền: đây là bốn con số người
        ta mở trang để nhìn trước, mỗi ô đọc xong trong một nhịp. Khối Dòng tiền
        là một chuỗi phép trừ — đọc nó cần dừng lại lâu hơn, nên nó đứng sau.
      */}
      <div className="mb-4">
        <StatStrip
          aria-label={`Chỉ số vận hành ${nhanKy}`}
          o={o}
          /* Kỳ ngoài mốc dữ liệu (link cũ `?ky=2017`): API trả 0, mà `0,00 $` đọc
           ra là "tụt về không". Hiện `—`; dòng "Không có số liệu trong kỳ này"
           nằm cạnh ô chọn kỳ (`ui/ChonKy`). */
          kyRong={kyRong}
        />
      </div>

      {/*
        Kỳ ngoài mốc dữ liệu (`?ky=2017`) thì không truyền số nào vào khối Dòng
        tiền — API trả 0, mà `0,00 $` ở đây đọc ra là "doanh thu tụt về không"
        chứ không phải "không có số".
      */}
      {coXemTien && (
        <div className="mb-4">
          <KhoiDongTien
            chuoi={kyRong ? undefined : them.data?.chuoiTien}
            chia={kyRong ? null : chia}
            nhanKy={nhanKy}
            deltaGop={coKyTruoc && !kyRong ? (d?.revenue?.deltaPercent ?? null) : null}
            nhanSoSanh={nhanSoKyTruoc(kyTrang)}
            soKenh={soKenh?.total}
            pctCuaToi={pctCuaToi}
            dangTai={them.isPending}
            loi={loiThem}
          />
        </div>
      )}

      {/*
        Tăng trưởng 3/5 · Cần xử lý 2/5 — **chỉ khi vùng nội dung ≥ 1100px**.

        Đo theo `@container/noi-dung`, không theo khung nhìn: trên laptop 1280px
        vùng nội dung chỉ 992px, chia 3/5 thì cột phải còn 390px — mỗi dòng cảnh
        báo cắt mất nửa tên kênh. Thu gọn thanh bên (nút ở thanh trên) đưa vùng
        nội dung lên 1160px và hai cột quay lại.

        `flex-wrap` + `basis-full` chứ không `grid`: dưới ngưỡng, hai khung xếp
        chồng đúng thứ tự đọc (trái lên trên) mà không cần khai lại số cột.
        Không dùng `flex-1` — nó là `flex: 1 1 0%` nên đè luôn `basis-*`. Dấu
        gạch dưới trong `calc(60%_-_0.5rem)` là **bắt buộc**: Tailwind đổi `_`
        thành khoảng trắng, mà `calc()` của CSS đòi khoảng trắng quanh `-` —
        viết liền (`calc(60%-0.5rem)`) là luật không hợp lệ, bị bỏ im lặng, và
        `basis-full` ở nấc dưới giữ nguyên nên hai khung **không bao giờ** về
        một hàng.
      */}
      <div className="mb-4 flex flex-wrap gap-4">
        <div className="min-w-0 grow basis-full @min-[1100px]/noi-dung:basis-[calc(60%_-_0.5rem)]">
          <KhungTangTruong
            duLieu={tangTruong.data}
            dangTai={tangTruong.isPending}
            loi={tangTruong.error?.message ?? null}
            huong={huongTang}
            theo={theoTang}
            onHuong={datHuongTang}
            onTheo={datTheoTang}
            chiKenhCuaToi={!toanCongTy}
            onThuLai={() => {
              void tangTruong.refetch();
            }}
          />
        </div>
        <div className="flex min-w-0 grow basis-full flex-col @min-[1100px]/noi-dung:basis-[calc(40%_-_0.5rem)]">
          <KhungCanXuLy
            duLieu={canhBao.data}
            dangTai={canhBao.isPending}
            loi={canhBao.error?.message ?? null}
            onThuLai={() => {
              void canhBao.refetch();
            }}
          />
        </div>
      </div>

      {/* Bảng kênh · Kênh theo nguồn tiền — cùng ngưỡng và cùng tỷ lệ với hàng trên. */}
      {coXemTien && (
        <div className="mb-4 flex flex-wrap gap-4">
          <div className="min-w-0 grow basis-full @min-[1100px]/noi-dung:basis-[calc(60%_-_0.5rem)]">
            <BangTopThucNhan
              duLieu={dongBangSap}
              dangTai={them.isPending}
              loi={loiThem}
              nhanKy={nhanKy}
              {...(chia === null ? {} : { xemChiaDayDu: chia.xemDayDu })}
            />
          </div>
          <div className="min-w-0 grow basis-full @min-[1100px]/noi-dung:basis-[calc(40%_-_0.5rem)]">
            <KhoiNguonTien
              duLieu={them.data?.theoNguonTien}
              dangTai={them.isPending}
              loi={loiThem}
            />
          </div>
        </div>
      )}

      {(coNhac || coSanXuat) && (
        <div className="flex flex-col gap-3">
          {coSanXuat && (
            <DongTomTat
              tieuDe="Sản xuất tuần này"
              to="/san-xuat"
              nhanLink="Mở bảng"
              /* "4 video trong lịch tuần này" gộp cả bài đã đăng lẫn bài còn
                 chờ duyệt, nên ghi chú tách rõ từng trạng thái (soát tay 12.0). */
              ghiChu={moTaTheoTrangThai(sanXuat)}
              phan={[
                { nhan: 'trên lịch', giaTri: sanXuat.thisWeek },
                ...Object.entries(sanXuat.byStage)
                  .filter(([, so]) => so > 0)
                  .map(([buoc, so]) => ({
                    nhan:
                      PRODUCTION_STAGE_LABEL[buoc as keyof typeof PRODUCTION_STAGE_LABEL] ?? buoc,
                    giaTri: so,
                  })),
              ]}
            />
          )}

          {coNhac && (
            <DongTomTat
              tieuDe="Kho nhạc"
              to="/kho-nhac"
              nhanLink="Mở kho"
              phan={[
                { nhan: 'Miss Records', giaTri: nhac.ownTracks },
                { nhan: 'kho hợp tác', giaTri: nhac.partnerTracks },
                ...(nhac.expiringSoon > 0
                  ? [{ nhan: 'sắp hết hạn', giaTri: nhac.expiringSoon, canhBao: true }]
                  : []),
              ]}
            />
          )}
        </div>
      )}
    </>
  );
}
